import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import InputMask from "react-input-mask";
import TextField from "@mui/material/TextField";
import * as Yup from "yup";
import { debounce } from "lodash";
import { useGetLocalAndLenderErrors } from "@/hooks/useRequiredFields/useGetLocalAndLenderErrors";
import { ERRORS_MESSAGE, VALIDATION_MESSAGE } from "../../constants";
import regex from "../../utils/regex";
import { addPhoneNumber, setRequiredFieldsErrors } from "../../store/slices/applicationFormSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";

export default function PhoneNumber({
  phoneNumber,
  disabled,
  label,
  fieldKey,
  saveContactNum,
  onValueChange,
  customerId,
  guarantorFieldErrors,
  setGuarantorFieldErrors,
  applicationId,
  requiredFieldName = "",
  isOutlined = false,
  fieldIndex = 0,
}) {
  const dispatch = useDispatch();
  const isSupplierPhone = fieldKey === "supplierPhone";
  const isEmployerPhone = fieldKey === "phoneNumberValue";
  // Employers of the same customer share the customer id, so the index keeps each employer's error separate.
  const phoneFieldName = isEmployerPhone ? `phoneNumber_${customerId}_${fieldIndex}` : `number_${customerId}`;
  const phoneFieldError = guarantorFieldErrors?.[phoneFieldName];

  const [allValues, setAllValue] = useState({
    number: phoneNumber?.number || phoneNumber,
  });

  useEffect(() => {
    if (phoneNumber?.number) {
      setAllValue({ number: phoneNumber?.number });
    } else if (phoneNumber) {
      setAllValue({ number: phoneNumber });
    } else {
      setAllValue({ number: "" });
    }
  }, [phoneNumber]);

  const validationSchema = Yup.object({
    number: isSupplierPhone
      ? Yup.string().matches(regex.phoneNumber, {
          message: VALIDATION_MESSAGE.australianMobileNumber,
          excludeEmptyString: true,
        })
      : Yup.string()
          .required("number field is required")
          .matches(regex.phoneNumber, VALIDATION_MESSAGE.australianMobileNumber),
    phoneNumber: Yup.string()
      .required("number field is required")
      .matches(regex.employerPhoneNumber, VALIDATION_MESSAGE.australianMobileNumber),
  });

  const debouncedValidation = useCallback(
    debounce(async (name, fieldName, value) => {
      try {
        if (isEmployerPhone) {
          await validationSchema.validateAt("phoneNumber", { phoneNumber: value });
        } else {
          await validationSchema.validateAt(name, { [name]: value });
        }
        if (isEmployerPhone) {
          dispatch(
            setRequiredFieldsErrors({
              ...guarantorFieldErrors,
              [fieldName]: "",
            }),
          );
        } else {
          setGuarantorFieldErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
        }
      } catch (error) {
        if (isEmployerPhone) {
          dispatch(
            setRequiredFieldsErrors({
              ...guarantorFieldErrors,
              [fieldName]: error.message,
            }),
          );
        } else {
          setGuarantorFieldErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: error.message,
          }));
        }
      }
    }, 300),
    [guarantorFieldErrors],
  );

  useEffect(() => () => debouncedValidation.cancel(), [debouncedValidation]);

  const handle = {
    onChangeField: async (e, nameField) => {
      const name = nameField.split("_")[0];
      if (name === "number" || name === "phoneNumber") {
        setAllValue({ ...allValues, number: e.replace(/\s+/g, "") });
        onValueChange?.(e.replace(/\s+/g, ""));
      } else {
        setAllValue({ ...allValues, [name]: e });
      }
      debouncedValidation(name, nameField, e);
    },
    blurFn: (fieldName, value) => {
      try {
        const inputValue = value.replace(/\s+/g, ""); // trim() not work
        if (isSupplierPhone && inputValue === "") {
          setGuarantorFieldErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "", supplierPhone: "" }));
          saveContactNum("supplierPhone", "");
          return;
        }

        if (phoneNumber?.number && phoneNumber?.number === inputValue) {
          return;
        }

        // Supplier callers may mirror the draft on every keystroke; it still needs saving on blur.
        if (!isSupplierPhone && phoneNumber && phoneNumber === inputValue) {
          return;
        }

        let fieldErr = guarantorFieldErrors[fieldName] || "";

        const isValid = isEmployerPhone
          ? regex.employerPhoneNumber.test(inputValue)
          : regex.phoneNumber.test(inputValue);

        if (isValid) {
          fieldErr = "";
        } else {
          fieldErr = isEmployerPhone
            ? VALIDATION_MESSAGE.australianMobileNumber
            : "Enter a valid Australian landline number.";
        }

        if (fieldErr === "") {
          if (isSupplierPhone) {
            saveContactNum("supplierPhone", inputValue);
            return;
          }

          if (isEmployerPhone) {
            saveContactNum("phoneNumber", inputValue);
            return;
          }

          if (!isSupplierPhone && label) {
            saveContactNum("contactNumber", inputValue);
            return;
          }

          dispatch(
            addPhoneNumber({
              data: {
                phoneNumberId: phoneNumber?._id,
                customerId,
                number: inputValue,
                applicationId,
              },
            }),
          ).then((res) => {
            if (res?.error) {
              dispatch(
                openSnackbar({
                  message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
                  intent: "danger",
                }),
              );
            }
          });
        }
      } catch (error) {
        const newErrors = {};
        error.inner.forEach((validationError) => {
          newErrors[validationError.path] = validationError.message;
        });
      }
    },
  };

  const { getCustomerOrEntityLocalAndLenderError, getAssetOrLoanDetailsLocalAndLenderError } =
    useGetLocalAndLenderErrors();
  const getLocalAndLenderError = ({ fieldError, fieldName, localFieldName }) =>
    isSupplierPhone
      ? getAssetOrLoanDetailsLocalAndLenderError({
          fieldError,
          localState: allValues,
          fieldName: "supplierPhone",
          localFieldName: "number",
        })
      : getCustomerOrEntityLocalAndLenderError({ fieldError, localState: allValues, fieldName, localFieldName });

  return (
    <InputMask
      mask={isEmployerPhone ? "9999999999999" : "9999 999 999"}
      value={allValues?.number || ""}
      disabled={disabled || false}
      onChange={(event) => handle.onChangeField(event?.target?.value, phoneFieldName)}
      onBlur={(e) => handle.blurFn(phoneFieldName, e?.target?.value)}
      maskChar=""
    >
      {() => (
        <TextField
          id={`phoneNumber_${customerId}`}
          label={label || "Mobile number"}
          variant={isOutlined ? "outlined" : "filled"}
          size="small"
          disabled={disabled || false}
          fullWidth
          name={`number_${customerId}`}
          error={Boolean(
            getLocalAndLenderError({
              fieldError: phoneFieldError,
              fieldName: requiredFieldName,
              localFieldName: "number",
            }),
          )}
          helperText={getLocalAndLenderError({
            fieldError: phoneFieldError,
            fieldName: requiredFieldName,
            localFieldName: "number",
          })}
        />
      )}
    </InputMask>
  );
}
